import { ApiError } from '@thyme/libs/src/api/apiRequest'
import { Method } from '@thyme/libs/src/api/types/api'
import { createUrlWithId } from '@thyme/libs/src/api/url'
import isArray from 'lodash-es/isArray'
import omit from 'lodash-es/omit'
import { defineStore } from 'pinia'
import { SaveState } from '@/legacy/types/api/api'
import {
  ApiActionOptions,
  ApiStoreState,
  ApiStoreDefinition,
  ApiStoreOptions,
  DataType,
  MAX_PAGE_SIZE,
  PassedSetupOptions,
  SetupOptions,
  MAX_PAGE_COUNT_SANITY_CHECK,
} from '@/legacy/types/api/apiBuilder'
import { apiRequest } from '@/libs/api/api'
import { thymeboxDefaultHeaders } from '@/libs/api/headers'
import { GenericListResponse } from '@/libs/api/types'
import { toIdMap } from '@/libs/format/objects'

/**
 *
 * @param storeName
 */
export function setDefaultHeaders(storeName: string) {
  return {
    'thymebox-api-store': storeName,
    ...thymeboxDefaultHeaders,
  }
}

/**
 * Transform list of items into combination of
 * IdMap and list for ease of updating list in place
 * @param idLocation
 */
export function transformListTo<
  T extends { [key: string | number | symbol]: any },
>(idLocation: keyof T) {
  return (d: { data: T[] }) => ({
    ...d,
    data: d.data.map((item: T) => item[idLocation] as string),
    idMap: toIdMap<T>(d.data, idLocation),
  })
}

/**
 * Creates a Pinia store for API operations with standardized CRUD methods.
 * @template Datum - Type for individual items (used in singular operations)
 * @template Data - Type for collections (defaults to Datum[], used in plural operations)
 * @template SingularResp - API response type for singular operations
 * @template ListResp - API response type for list operations
 * @param storeName - Unique identifier for the store
 * @param url - Base API endpoint URL (supports :id placeholders)
 * @param options - Configuration options for transformations and defaults
 * @returns Pinia store with the following:
 *
 * **State:**
 * - `data` - Collection data from list operations (PLURAL)
 * - `datum` - Individual item from retrieve/create/update operations (SINGULAR)
 * - `isLoading` - Request in progress indicator
 * - `isLoaded` - At least one successful request completed
 * - `error` - Last error encountered
 * - `queryMetadata` - Pagination and query metadata
 * - `idMap` - ID-to-item mapping for efficient updates
 *
 * **Key Actions:**
 * - `list()` - GET collection from API → PLURAL
 * - `listAll()` - GET all items with automatic pagination → PLURAL
 * - `retrieve(ids)` - GET single item by ID → SINGULAR
 * - `create(body)` - POST new item → SINGULAR
 * - `update(ids, body)` - PUT update existing item → SINGULAR
 * - `partialUpdate(ids, body)` - PATCH partial update → SINGULAR
 * - `delete(ids)` - DELETE item by ID → SINGULAR
 * - `updateData()` - Manually update store state
 * - `updateInPlace()` - Update item in idMap without full refresh
 *
 * **URL ID Replacement:**
 * Use `:id` placeholders in URL for dynamic ID substitution.
 * Example: `"api/foo/:id/bar/:id"` with `ids = ["1", "2"]` → `"api/foo/1/bar/2"`
 */
export default <
  Datum,
  Data = Datum[],
  SingularResp = Datum,
  ListResp = GenericListResponse<SingularResp>,
>(
  storeName: string,
  url: string,
  options: ApiStoreOptions<Datum, Data, SingularResp, ListResp> = {}
) =>
  defineStore(storeName, {
    state: (): ApiStoreState<Datum, Data> => ({
      data: null,
      datum: null,
      isLoading: false,
      isLoaded: false,
      error: null,
      queryMetadata: null,
      idMap: null,
    }),
    getters: {
      computedData: (state?: ApiStoreState<Datum, Data>) => {
        if (!state) return null
        if (isArray(state.data) && state.idMap) {
          return state.data.map((id: string) => (state.idMap ?? {})[id])
        }
        return state.data
      },
    },
    actions: {
      reset() {
        this.isLoading = false
        this.isLoaded = false
        this.error = null
        this.queryMetadata = null
        this.data = null
        this.datum = null
      },
      transformData(
        data: ListResp | null | undefined
      ): ApiStoreState<Datum, Data> {
        let transformed: Partial<ApiStoreState<Datum, Data>> &
          Pick<ApiStoreState<Datum, Data>, 'data'>
        if (options.transformData && !!data) {
          const rawTransformed = options.transformData(
            data,
            this.$state as ApiStoreState<Datum, Data>
          )
          // Avoid undefined `data` in state
          transformed = { ...rawTransformed, data: rawTransformed.data ?? null }
        } else {
          transformed = { data: (data as Data) ?? null }
        }
        return {
          ...omit(this.$state, 'data', 'datum'),
          // explicitly append for UnWrap<T> Type issues
          datum: this.$state.datum as Datum | null,
          ...transformed,
        }
      },
      transformDatum(
        datum: SingularResp | null | undefined
      ): ApiStoreState<Datum, Data> {
        let transformed: Partial<ApiStoreState<Datum, Data>> &
          Pick<ApiStoreState<Datum, Data>, 'datum'>
        if (options.transformDatum && !!datum) {
          const rawTransformed = options.transformDatum(
            datum,
            this.$state as ApiStoreState<Datum, Data>
          )
          // Avoid undefined `datum` in state
          transformed = {
            ...rawTransformed,
            datum: rawTransformed.datum ?? null,
          }
        } else {
          transformed = { datum: (datum as Datum) ?? null }
        }
        return {
          ...omit(this.$state, 'datum', 'data'),
          // explicitly append for UnWrap<T> Type issues
          data: this.$state.data as Data | null,
          ...transformed,
        }
      },
      transform(data: SingularResp | ListResp | null, type: DataType) {
        if (type === DataType.SINGULAR) {
          // @ts-ignore
          return this.transformDatum(data as SingularResp)
        } else if (type === DataType.PLURAL) {
          // @ts-ignore
          return this.transformData(data as ListResp)
        }
      },
      setPending(saveState?: SaveState) {
        this.isLoading = true
        if (saveState) {
          saveState.isSaving = true
          saveState.lastCallAt = new Date()
        }
      },
      setSuccess(
        data: SingularResp | ListResp | null,
        type: DataType,
        saveState?: SaveState
      ) {
        this.updateData(data, type)
        this.isLoading = false
        this.isLoaded = true
        this.error = null

        // since saveState may be monitoring some subset of calls
        // by a consumer, update it separately from the store's
        // error handling
        if (saveState) {
          saveState.isSaving = false
          saveState.error = null
        }
      },
      setError(error: ApiError | Error, saveState?: SaveState) {
        this.error = error
        this.isLoading = false
        this.isLoaded = true

        // since saveState may be monitoring some subset of calls
        // by a consumer, update it separately from the store's
        // error handling
        if (saveState) {
          saveState.isSaving = false
          saveState.error = error
        }
      },
      isQueued() {
        return !this.isLoaded && this.isLoading
      },
      /**
       * Break large global "fetch all" list calls into segments
       *  DOES NOT WORK WITH PAGE_TOKEN
       * @param method
       * @param url
       * @param options
       * @param setup
       */
      async splitApiActionCall(
        method: Method,
        url: string,
        options: ApiActionOptions,
        setup?: SetupOptions
      ) {
        // ensure check passes first time
        let itemsToGrab = MAX_PAGE_SIZE + 1
        // @ts-ignore params is guaranteed here
        options.params.page_length = options.params.page_length ?? MAX_PAGE_SIZE
        // @ts-ignore params is guaranteed here
        options.params.page_number = options.params.page_number ?? 1
        let grabbedItems = 0
        let data: any[] = []
        const maxListAllPages =
          setup?.maxListAllPagesAllowed ?? MAX_PAGE_COUNT_SANITY_CHECK
        while (itemsToGrab - grabbedItems > 0) {
          if (options.params?.page_number > maxListAllPages) {
            console.error(
              'Exceeded maximum page count sanity check',
              storeName,
              url,
              options
            )
            break
          }
          grabbedItems += MAX_PAGE_SIZE
          const response = await apiRequest<any>(method, url, options)
          data = [...data, ...response.data]
          itemsToGrab = response.queryMetadata?.total ?? itemsToGrab
          // @ts-ignore params is guaranteed here
          options.params.page_number++
        }
        return { data, queryMetadata: { total: itemsToGrab } }
      },
      async apiAction(
        method: Method,
        url: string,
        dataType: DataType,
        options: ApiActionOptions,
        setup?: SetupOptions
      ) {
        const saveState = setup?.saveState
        this.setPending(saveState)
        try {
          let respData
          if (setup?.fetchAll) {
            respData = await this.splitApiActionCall(
              method,
              url,
              options,
              setup
            )
          } else {
            respData = await apiRequest<any>(method, url, options)
          }
          this.setSuccess(respData, dataType, saveState)
          return respData
        } catch (err) {
          if (err instanceof Error) {
            console.error(err.message)
            this.setError(err, saveState)
          } else {
            console.error(err)
          }
          /**
           *  bubbleErrorThrow: true -> bubble error for try/catch workflows
           *  WARNING WILL CAUSE SERIOUS PAGE BREAKAGE IF NOT CAUGHT
           */
          if (setup?.bubbleErrorThrow) {
            throw err
          }
        }
      },
      updateData(newData: SingularResp | ListResp | null, type: DataType) {
        Object.assign(this.$state, this.transform(newData, type))
      },
      /**
       * Bypass transform
       * @param data
       * @deprecated use normal updateData flow instead
       */
      setData(data: Datum | Data) {
        Object.assign(this.$state, {
          ...omit(this.$state, 'data', 'datum'),
          // explicitly append for UnWrap<T> Type issues
          ...(this.$state.datum as Datum | null),
          data,
        })
      },
      /**
       * replace item in idMap with newly updated version
       * @param param0
       * @param param0.id
       * @param param0.item
       */
      async updateInPlace({ id, item }: { id: string; item: Datum }) {
        // if idMap is not set, we are not tracking items via idMap
        // and do not want to partially create idMap partway through.
        // if you did, it would break the if statement in computedData
        if (this.idMap) {
          this.idMap[id] = item
        }
      },
      async list({
        headers = {},
        params = {},
        ids = null,
        metaOptions,
      }: {
        headers?: { [key: string]: string }
        params?: { [key: string]: any }
        ids?: string | string[] | null
        metaOptions?: PassedSetupOptions
      }) {
        return await this.apiAction(
          'GET' as Method,
          createUrlWithId(url, ids),
          DataType.PLURAL,
          {
            headers: {
              ...setDefaultHeaders(storeName),
              ...options.headers,
              ...headers,
            },
            params: { ...options.params, ...params },
          },
          metaOptions
        )
      },
      async listAll({
        headers = {},
        params = {},
        ids = null,
        metaOptions,
      }: {
        headers?: { [key: string]: string }
        params?: { [key: string]: any }
        ids?: string | string[] | null
        metaOptions?: PassedSetupOptions
      }) {
        return await this.apiAction(
          'GET' as Method,
          createUrlWithId(url, ids),
          DataType.PLURAL,
          {
            headers: {
              ...setDefaultHeaders(storeName),
              ...options.headers,
              ...headers,
            },
            params: { ...options.params, ...params },
          },
          { ...metaOptions, fetchAll: true }
        )
      },
      async retrieve({
        headers = {},
        params = {},
        ids,
        metaOptions,
      }: {
        headers?: { [key: string]: string }
        params?: { [key: string]: any }
        ids: string | string[]
        metaOptions?: PassedSetupOptions
      }) {
        return await this.apiAction(
          'GET' as Method,
          createUrlWithId(url, ids),
          DataType.SINGULAR,
          {
            headers: {
              ...setDefaultHeaders(storeName),
              ...options.headers,
              ...headers,
            },
            params: { ...options.params, ...params },
          },
          metaOptions
        )
      },
      async create({
        headers = {},
        params = {},
        body = {},
        ids = null,
        file = null,
        metaOptions,
      }: {
        headers?: { [key: string]: string }
        params?: { [key: string]: any }
        body?: { [key: string]: any }
        ids?: string | string[] | null
        file?: File | null
        metaOptions?: PassedSetupOptions
      }) {
        return await this.apiAction(
          'POST' as Method,
          createUrlWithId(url, ids),
          DataType.SINGULAR,
          {
            headers: {
              ...setDefaultHeaders(storeName),
              ...options.headers,
              ...headers,
            },
            params: { ...options.params, ...params },
            body,
            file,
          },
          metaOptions
        )
      },
      async upsert({
        headers = {},
        params = {},
        body = {},
        ids = null,
        metaOptions,
      }: {
        headers?: { [key: string]: string }
        params?: { [key: string]: any }
        body?: { [key: string]: any }
        ids?: string | string[] | null
        metaOptions?: PassedSetupOptions
      }) {
        return await this.apiAction(
          'PUT' as Method,
          createUrlWithId(url, ids),
          DataType.SINGULAR,
          {
            headers: {
              ...setDefaultHeaders(storeName),
              ...options.headers,
              ...headers,
            },
            params: { ...options.params, ...params },
            body,
          },
          metaOptions
        )
      },
      async update({
        headers = {},
        params = {},
        body = {},
        ids = null,
        metaOptions,
      }: {
        headers?: { [key: string]: string }
        params?: { [key: string]: any }
        body?: { [key: string]: any }
        ids?: string | string[] | null
        metaOptions?: PassedSetupOptions
      }) {
        if (!ids) {
          this.setError(new Error(`Id required for update on ${url}`))
          return
        }
        return await this.apiAction(
          'PUT' as Method,
          createUrlWithId(url, ids),
          DataType.SINGULAR,
          {
            headers: {
              ...setDefaultHeaders(storeName),
              ...options.headers,
              ...headers,
            },
            params: { ...options.params, ...params },
            body,
          },
          metaOptions
        )
      },
      async partialUpdate({
        headers = {},
        params = {},
        body = {},
        ids = null,
        extraHeaders = {},
        metaOptions,
      }: {
        headers?: { [key: string]: string }
        params?: { [key: string]: any }
        body?: { [key: string]: any }
        ids?: string | string[] | null
        extraHeaders?: { [key: string]: string }
        metaOptions?: PassedSetupOptions
      }) {
        if (!ids) {
          this.setError(new Error(`Id required for partialUpdate on ${url}`))
          return
        }

        return await this.apiAction(
          'PATCH' as Method,
          createUrlWithId(url, ids),
          DataType.SINGULAR,
          {
            headers: {
              ...setDefaultHeaders(storeName),
              ...options.headers,
              ...headers,
              ...extraHeaders,
            },
            params: { ...options.params, ...params },
            body,
          },
          metaOptions
        )
      },
      async delete({
        headers = {},
        ids = null,
        metaOptions,
        params = {},
      }: {
        headers?: { [key: string]: string }
        ids?: string | string[] | null
        metaOptions?: PassedSetupOptions
        params?: { [key: string]: any }
      }) {
        if (!ids) {
          this.setError(new Error(`Id required for delete on ${url}`))
          return
        }
        return await this.apiAction(
          'DELETE' as Method,
          createUrlWithId(url, ids),
          DataType.SINGULAR,
          {
            headers: {
              ...setDefaultHeaders(storeName),
              ...options.headers,
              ...headers,
            },
            params: { ...options.params, ...params },
          },
          metaOptions
        )
      },
    },
  }) as ApiStoreDefinition<Datum, Data>
